import { HttpParams } from "@angular/common/http";
import { forwardRef, Inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { Permissions } from "../common/constants";
import { TEMPLATE_CONTENT, USER_DASHBOARDS_V2, USER_TEMPLATES_V2, USER_UI_PROFILE_PAGES_V2, USER_UI_PROFILES_V2 } from "../common/endpoints";
import { Dashboard, PagedList, Template, UiProfile, UiProfilePage, UiProfilePageNavigationType, UiProfilePageType } from "../model";
import { AuthenticationService } from "./authentication.service";
import { HttpService } from "./http.service";

export interface TemplateContent {
    found: boolean;
    content: string;
}

@Injectable()
export class UiService {

    private profile: UiProfile;
    private pages: UiProfilePage[] = [];
    private loaded: Promise<void>;
    private templateContentCache: { [name: string]: TemplateContent } = {};

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService
    ) { }

    load(): void {
        this.loaded = new Promise(resolve => {
            let params = new HttpParams().set('page', '0').set('size', '1');
            firstValueFrom(this.httpService.get<PagedList<UiProfile>>(USER_UI_PROFILES_V2, params)).then(profiles => {
                if (profiles.content.length == 0) {
                    console.error('No profile configured for current User');
                    resolve();
                } else {
                    this.profile = profiles.content[0];
                    let httpParams = new HttpParams().set('uiProfileId', this.profile.id).set('page', '0').set('size', '50');
                    firstValueFrom(this.httpService.get<PagedList<UiProfilePage>>(USER_UI_PROFILE_PAGES_V2, httpParams)).then(pages => {
                        let unsortedPages = pages.content;
                        this.pages = (this.profile.sidebarPageIds || []).filter(pageId => unsortedPages.some(p => p.id == pageId))
                            .map(pageId => unsortedPages.find(p => p.id == pageId));
                        resolve();
                    });
                }
            });
        });
    }

    waitForLoaded(): Promise<void> {
        return this.loaded;
    }

    getUserPagedDashboards(thingId: string, page: number, size: number): Promise<PagedList<Dashboard>> {
        let params = new HttpParams().set('thingId', thingId).set('page', page + '').set('size', size + '');
        return firstValueFrom(this.httpService.get<PagedList<Dashboard>>(USER_DASHBOARDS_V2, params));
    }

    getUserPagedTemplates(name: string, page: number, size: number, thingDefinitionId?: string): Promise<PagedList<Template>> {
        let params = new HttpParams().set('name', name).set('page', page + '').set('size', size + '');
        if (thingDefinitionId) {
            params = params.set('thingDefinitionId', thingDefinitionId);
        }
        return this.doGetUserPagedTemplates(params);
    }

    private doGetUserPagedTemplates(params: HttpParams): Promise<PagedList<Template>> {
        return firstValueFrom(this.httpService.get<PagedList<Template>>(USER_TEMPLATES_V2, params));
    }

    getUiProfile(): UiProfile {
        return this.profile;
    }

    getUiProfilePages(): UiProfilePage[] {
        return this.pages;
    }

    getSidebarPages(): UiProfilePage[] {
        return this.getUiProfilePages().filter(p => p.navigation == UiProfilePageNavigationType.SIDEBAR_MENU && this.isPageVisible(p));
    }

    private isPageVisible(page: UiProfilePage): boolean {
        if (page.type == UiProfilePageType.SUBSCRIPTIONS && !this.authenticationService.hasPermission(Permissions.READ_SUBSCRIPTION)) {
            return false;
        }
        if (page.type == UiProfilePageType.DATA_MANAGEMENT && !this.authenticationService.hasPermission(Permissions.ACCOUNT_ADMINISTRATOR)) {
            return false;
        }
        if (page.type == UiProfilePageType.TICKETS && !this.authenticationService.hasPermission(Permissions.READ_TICKET)) {
            return false;
        }
        if (!page.visibilityCondition) {
            return true;
        }
        let visibilityCondition = this.normalizeVisibilityCondition(page.visibilityCondition);
        try {
            return eval(visibilityCondition);
        } catch {
            return false;
        }
    }

    normalizeVisibilityCondition(visibilityCondition: string): string {
        let appUtilsFunctions = ['getUser', 'isMobile', 'isTablet', 'getThing', 'getThingDefinition', 'getCustomer', 'getLocation', 'getPartner', 'getServiceLevel', 'getTestSession',
            'getTestSessionStartDate', 'getTestSessionStopDate', 'getUserCustomer', 'getCustomerServiceLevel', 'getWorkSession', 'getTags',
            'getCurrentTimestamp', 'getAlert', 'getTenant', 'hasPlan', 'hasAddOn', 'hasFeature', 'getSubscription', 'getParentThing', 'getRootThing'];
        appUtilsFunctions.forEach(f => visibilityCondition = (visibilityCondition as any).replaceAll(f, 'appUtils.' + f));
        return visibilityCondition;
    }

    getTopbarLeft(): UiProfilePage {
        let topBar: UiProfilePage = this.getUiProfilePages().find(p => p.navigation == UiProfilePageNavigationType.TOPBAR_LEFT);
        if (topBar && this.isTopBarVisible(topBar)) {
            return topBar;
        }
        return null;
    }

    getTopbarRight(): UiProfilePage {
        let topBar: UiProfilePage = this.getUiProfilePages().find(p => p.navigation == UiProfilePageNavigationType.TOPBAR_RIGHT);
        if (topBar && this.isTopBarVisible(topBar)) {
            return topBar;
        }
        return null;
    }

    private isTopBarVisible(topBar: UiProfilePage): boolean {
        if (!topBar.visibilityCondition) {
            return true;
        }
        let visibilityCondition = this.normalizeVisibilityCondition(topBar.visibilityCondition);
        try {
            return eval(visibilityCondition);
        } catch {
            return false;
        }
    }

    getUiProfilePageByUrl(url: string): UiProfilePage {
        return this.pages.find(p => p.urlPath == url);
    }

    getFirstUiProfilePageByType(type: UiProfilePageType): UiProfilePage {
        return this.pages.find(p => p.type == type);
    }

    getContentByTemplateName(name: string, thingDefinitionId?: string, includeGeneral: boolean = false): Promise<string> {
        return this.getTemplateContentSelection(name, thingDefinitionId, includeGeneral).then(selection => selection.content);
    }

    // like getContentByTemplateName, but tells a found empty template from a miss
    getTemplateContentSelection(name: string, thingDefinitionId?: string, includeGeneral: boolean = false): Promise<TemplateContent> {
        let templateContentKey = this.getTemplateContentKey(name, thingDefinitionId);
        if (this.templateContentCache.hasOwnProperty(templateContentKey)) {
            return Promise.resolve(this.templateContentCache[templateContentKey]);
        }
        return this.getUserPagedTemplates(name, 0, 1, thingDefinitionId).then(templatePagedList => {
            if (templatePagedList.content?.length) {
                let template = templatePagedList.content[0];
                return this.getTemplateContentAndSaveInCache(template.id, templateContentKey);
            } else if (thingDefinitionId && includeGeneral) {
                // hierarchy miss: not cached, retry in General
                return this.getTemplateContentSelection(name);
            } else {
                // only a General miss is cached
                const miss: TemplateContent = { found: false, content: '' };
                if (!thingDefinitionId) {
                    this.templateContentCache[templateContentKey] = miss;
                }
                return miss;
            }
        });
    }

    getTemplateContentKey(name: string, thingDefinitionId?: string) {
        let thingDefSuffix = thingDefinitionId ? '_' + thingDefinitionId : '';
        let templateContentKey = name + thingDefSuffix;
        return templateContentKey;
    }

    getTemplateContentAndSaveInCache(templateId: string, templateContentKey: string): Promise<TemplateContent> {
        return firstValueFrom(this.httpService.getText(TEMPLATE_CONTENT.replace('{id}', templateId))).then(content => {
            const selection = { found: true, content: content };
            this.templateContentCache[templateContentKey] = selection;
            return selection;
        });
    }

    getEventDetailsTemplate(): string {
        return this.profile.activeAlertDetailsTemplate;
    }

    getTopbarPages(): UiProfilePage[] {
        return this.getUiProfilePages().filter(p => p.navigation == UiProfilePageNavigationType.TOPBAR && this.isPageVisible(p));
    }

    getUserMenuPages(): UiProfilePage[] {
        return this.getUiProfilePages().filter(p => p.navigation == UiProfilePageNavigationType.USER_MENU && this.isPageVisible(p));
    }

    getRecursivelyAllUserTemplates(thingDefinitionId?: string, page?: number, templates?: Template[]): Promise<Template[]> {
        if (!page) {
            page = 0;
        }
        if (!templates) {
            templates = [];
        }

        let params = new HttpParams().set('page', page).set('size', 200).set('sort', 'name,asc').set('skipInterceptor', true);
        if (thingDefinitionId) {
            params = params.set('thingDefinitionId', thingDefinitionId)
        }
        return this.doGetUserPagedTemplates(params).then(pagedList => {
            templates = templates.concat(pagedList.content);
            if (pagedList.last) {
                return templates;
            }
            return this.getRecursivelyAllUserTemplates(thingDefinitionId, page, templates);
        });
    }

}